import { type FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { faWeightScale } from "@fortawesome/pro-regular-svg-icons";
import { useFormik } from "formik";
import {
  activityOptions,
  genreOptions,
  objectiveOptions,
  workOptions,
  sicleOption,
  TrainingWizardPlan,
  trainingWizardPlans,
} from "@/app/training-wizard/training-wizard";
import { Button } from "@/shared/components/atoms/Button";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { RadioButtonGroup } from "@/shared/components/atoms/RadioButtonGroup";
import { Slider } from "@/shared/components/atoms/Slider";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { Modal } from "@/shared/components/organisms/Modal";
import {
  trainingWizardInitialValuesFactory,
  trainingWizardValidationSchema,
  TrainingWizardValues,
} from "@/shared/schemas/training.schema";
import calculateBodyMetrics from "../utils/Imc-calculator";
import {
  getTrainingRecommendation,
  TrainingRecommendationData,
} from "../utils/WizardRecomend";

const converterInchesOrLb = (
  value: number | string,
  unit: string | undefined
) => {
  let numericValue = typeof value === "string" ? parseFloat(value) : value;
  numericValue =
    typeof numericValue === "number" && !isNaN(numericValue) ? numericValue : 0;
  if (typeof unit !== "string") {
    return numericValue;
  }

  const normalizedUnit = unit.trim().toLowerCase();

  switch (normalizedUnit) {
    case "inches":
    case "in":
      return numericValue * 2.54;
    case "lb":
    case "lbs":
    case "pounds":
      return numericValue * 0.453592;
    default:
      return numericValue;
  }
};

// Función para seleccionar el plan
const getTrainingPlan = (
  age: number,
  gender: string,
  activity: string | undefined,
  objective: string | undefined,
  work?: string
): TrainingWizardPlan | null => {
  if (activity === "active") {
    if (gender === "hombre") {
      if (objective === "hypertrophy") {
        if (age >= 16 && age <= 20)
          return trainingWizardPlans.HYPERTROPHY_ACTIVE_MAN_16_20;
        if (age >= 21 && age <= 38)
          return trainingWizardPlans.HYPERTROPHY_ACTIVE_MAN_21_38;
        if (age >= 39 && age <= 100)
          return trainingWizardPlans.HYPERTROPHY_ACTIVE_MAN_39_50;
      } else if (objective === "definition") {
        if (age >= 16 && age <= 20)
          return trainingWizardPlans.DEFINITION_ACTIVE_MAN_16_20;
        if (age >= 21 && age <= 38)
          return trainingWizardPlans.DEFINITION_ACTIVE_MAN_21_38;
        if (age >= 39 && age <= 100)
          return trainingWizardPlans.DEFINITION_ACTIVE_MAN_39_50;
      }
    } else if (gender === "mujer") {
      if (objective === "hypertrophy") {
        if (age >= 16 && age <= 100)
          return trainingWizardPlans.HYPERTROPHY_ACTIVE_WOMAN_16_48;
      } else if (objective === "definition") {
        if (age >= 16 && age <= 100)
          return trainingWizardPlans.DEFINITION_ACTIVE_WOMAN_16_48;
      }
    }
    if (objective === "health" && work) {
      if (age >= 0 && age <= 100) {
        if (work === "sit") return trainingWizardPlans.SALUD_SIT_ACTIVE;
        if (work === "standing")
          return trainingWizardPlans.SALUD_STANDING_ACTIVE;
        if (work === "crouching") return trainingWizardPlans.SALUD_DOWN_ACTIVE;
      }
    }
  } else if (activity === "sedentary") {
    if (gender === "hombre") {
      if (objective === "hypertrophy") {
        if (age >= 16 && age <= 20)
          return trainingWizardPlans.HYPERTROPHY_SEDENTARY_MAN_16_20;
        if (age >= 21 && age <= 38)
          return trainingWizardPlans.HYPERTROPHY_SEDENTARY_MAN_21_38;
        if (age >= 39 && age <= 99)
          return trainingWizardPlans.HYPERTROPHY_SEDENTARY_MAN_39_50;
      } else if (objective === "definition") {
        if (age >= 16 && age <= 20)
          return trainingWizardPlans.DEFINITION_SEDENTARY_MAN_16_20;
        if (age >= 21 && age <= 38)
          return trainingWizardPlans.DEFINITION_SEDENTARY_MAN_21_38;
        if (age >= 39 && age <= 99)
          return trainingWizardPlans.DEFINITION_SEDENTARY_MAN_39_50;
      }
    } else if (gender === "mujer") {
      if (objective === "hypertrophy") {
        if (age >= 16 && age <= 99)
          return trainingWizardPlans.HYPERTROPHY_SEDENTARY_WOMAN_16_48;
      } else if (objective === "definition") {
        if (age >= 16 && age <= 99)
          return trainingWizardPlans.DEFINITION_SEDENTARY_WOMAN_16_48;
      }
    }
    if (objective === "health" && work) {
      if (age >= 0 && age <= 99) {
        if (work === "sit") return trainingWizardPlans.SALUD_SIT_SEDENTARY;
        if (work === "standing")
          return trainingWizardPlans.SALUD_STANDING_SEDENTARY;
        if (work === "crouching")
          return trainingWizardPlans.SALUD_DOWN_SEDENTARY;
      }
    }
  }

  console.log("Ningún plan seleccionado para:", {
    age,
    gender,
    activity,
    objective,
    work,
  });
  return null;
};

export type TrainingWizardProps = {
  visible?: boolean;
  setVisible?: (visible: boolean) => void;
  user: any;
  personalInformation?: any;
  height?: any;
  weight?: any;
  onSubmit: (values: TrainingWizardValues) => void;
  loader?: boolean;
};

export const TrainingWizard: FC<TrainingWizardProps> = ({
  visible = false,
  setVisible,
  personalInformation,
  user,
  weight,
  height,
  onSubmit,
  loader = false,
}) => {
  const { t } = useTranslation();
  const [recommendationVisible, setRecommendationVisible] = useState(false);

  const user_branch = user?.data?.user?.branch;

  const [unidades, setUnidades] = useState({
    weight: "kg",
    height: "cm",
  });

  const [recommendation, setRecommendation] = useState({
    neck: 0,
    waist: 0,
  });

  const formik = useFormik({
    initialValues: trainingWizardInitialValuesFactory({
      age: personalInformation?.age,
      genre: personalInformation?.sex?.toLowerCase() || "hombre",
      height: height?.height,
      weight: weight?.weight,
      position: "",
    }),
    enableReinitialize: true,
    validationSchema: trainingWizardValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      console.log("Formulario enviado:", values);
      onSubmit({
        ...values,
        weight: converterInchesOrLb(values.weight, unidades.weight),
        height: converterInchesOrLb(values.height, unidades.height),
      });
    },
  });

  // Determinar actividad automáticamente
  const autoActivity: any = useMemo(() => {
    if (
      formik.values.objective === "health" &&
      formik.values.work === "crouching"
    ) {
      return "active";
    }
    return formik.values.activity;
  }, [formik.values.objective, formik.values.work, formik.values.activity]);

  const trainingPlan = useMemo(() => {
    console.log("Valores actuales del formulario:", {
      age: formik.values.age,
      gender: formik.values.genre,
      activity: autoActivity,
      objective: formik.values.objective,
      work: formik.values.work,
    });
    return getTrainingPlan(
      formik.values.age,
      formik.values.genre,
      autoActivity,
      formik.values.objective,
      formik.values.work
    );
  }, [
    formik.values.age,
    formik.values.genre,
    autoActivity,
    formik.values.objective,
    formik.values.work,
  ]);

  const sliderTexts = useMemo(() => {
    if (
      !trainingPlan ||
      autoActivity !== "active" ||
      !formik.values.experience
    ) {
      console.log("Slider no renderizado - Motivo:", {
        trainingPlan: !!trainingPlan,
        activity: autoActivity,
        experience: formik.values.experience,
      });
      return null;
    }
    const experience = formik.values.experience;
    const validPosition = Object.keys(trainingPlan)
      .map(Number)
      .filter((key) => !isNaN(key) && key <= experience)
      .sort((a, b) => b - a)[0];
    const texts = validPosition ? trainingPlan[validPosition] : null;
    console.log(
      "Slider - Experiencia:",
      experience,
      "Posición:",
      validPosition,
      "Textos:",
      texts
    );
    return texts
      ? {
          label: t(texts.experience),
          description: t(texts.description),
          days: t(texts.days),
          text: t(texts.text),
        }
      : null;
  }, [trainingPlan, autoActivity, formik.values.experience, t]);

  // Opciones de días para planes con "days"
  const daysOptions = useMemo(() => {
    if (trainingPlan?.plans?.days?.length > 0) {
      return trainingPlan?.plans.days.map((plan: any) => ({
        key: plan.days,
        text: `${plan.days} días`,
        value: plan.days,
      }));
    }
    return [];
  }, [trainingPlan]);

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      activity: undefined,
      experience: undefined as any,
      /* @ts-ignore */
      position: undefined,
      plan: undefined,
      days: undefined,
      work: undefined,
      cicle: undefined as any,
    });
  }, [formik.values.objective]);


  
  useEffect(() => {
    if (formik.values.objective === "health" && formik.values.work) {
      if (formik.values.work === "crouching") {
        formik.setFieldValue("activity", "active");
        if (trainingPlan && !formik.values.experience) {
          formik.setFieldValue("experience", trainingPlan.min);
          console.log(
            "Work 'crouching' - Actividad: active, Experiencia:",
            trainingPlan.min
          );
        }
      } else {
        formik.setFieldValue("experience", undefined);
        formik.setFieldValue("days", undefined);
        //position 
    
        console.log(
          "Work 'sitting' o 'standing' - Esperando selección de actividad"
        );
      }
    }
  }, [formik.values.work, trainingPlan]);

  useEffect(() => {
    if (
      trainingPlan &&
      autoActivity === "active" &&
      !formik.values.experience
    ) {
      const minExperience = trainingPlan.min;
      formik.setFieldValue("experience", minExperience);
      console.log("Actividad 'active' - Experiencia inicial:", minExperience);
    }
  }, [trainingPlan, autoActivity]);

  // Actualizamos posición y plan para "active"
/*   useEffect(() => {
    if (
      trainingPlan &&
      autoActivity === "active" &&
      formik.values.experience &&
      trainingPlan.getPlan
    ) {
      const position = trainingPlan.getPlan(formik.values.experience);
      const plan = trainingPlan.table_plan;
      formik.setFieldValue("position", position);
      if (!formik.values.days && trainingPlan.plans?.days) {
        formik.setFieldValue("plan", trainingPlan.plans.days[0].plan);
      } else {
        formik.setFieldValue("plan", plan);
      }
      console.log(
        "Actualización (active) - Posición:",
        position,
        "Plan:",
        plan
      );
    }
  }, [trainingPlan, autoActivity, formik.values.experience]); */

  useEffect(() => {
    if (
      trainingPlan &&
      autoActivity === "sedentary" &&
      formik.values.objective === "definition" &&
      trainingPlan.plans?.days?.length > 0
    ) {
      if (!formik.values.days) {
        const defaultDay = trainingPlan.plans.days[0];
        formik.setFieldValue("days", defaultDay.days);
        formik.setFieldValue("plan", defaultDay.plan);
        formik.setFieldValue("position", defaultDay.position);
        console.log("Definición (sedentary) - Valores iniciales:", {
          days: defaultDay.days,
          plan: defaultDay.plan,
          position: defaultDay.position,
        });
      } else {
        const selectedDay = trainingPlan.plans.days.find(
          (d: any) => d.days === formik.values.days
        );
        if (selectedDay) {
          formik.setFieldValue("plan", selectedDay.plan);
          formik.setFieldValue("position", selectedDay.position);
          console.log("Definición (sedentary) - Actualizado:", {
            days: selectedDay.days,
            plan: selectedDay.plan,
            position: selectedDay.position,
          });
        }
      }
      // Limpiar experience, ya que no se usa para definición sedentaria
      formik.setFieldValue("experience", undefined);
    }
  }, [trainingPlan, autoActivity, formik.values.objective, formik.values.days]);

  useEffect(() => {
    if (
      trainingPlan &&
      autoActivity === "active" &&
      formik.values.experience &&
      trainingPlan.getPlan
    ) {
      const position = trainingPlan.getPlan(formik.values.experience);
      const plan = trainingPlan.table_plan;
      formik.setFieldValue("position", position);

      if (formik.values.objective === "health" && trainingPlan.plans?.days) {
        const sortedDays = trainingPlan.plans.days.sort(
          (a: any, b: any) => a.days - b.days
        );
        const selectedDay =
          sortedDays.find((d: any) => d.days == formik.values.days) ||
          sortedDays[0];
        formik.setFieldValue("days", selectedDay.days);
        formik.setFieldValue("plan", selectedDay.plan);
        console.log(
          "Health (active) - Posición:",
          position,
          "Plan:",
          selectedDay.plan,
          "Días asignados:",
          selectedDay.days
        );
      } else {
        if (!formik.values.days && trainingPlan.plans?.days) {
          formik.setFieldValue("plan", trainingPlan?.plans?.days?.[0]?.plan);
        } else {
          formik.setFieldValue("plan", plan);
        }
        console.log(
          "Actualización (active, no health) - Posición:",
          position,
          "Plan:",
          plan
        );
      }
    }
  }, [
    trainingPlan,
    autoActivity,
    formik.values.experience,
    formik.values.days,
    formik.values.objective,
  ]);

  const [imc, setImc] = useState(0);
  const [bodyFatPercentage, setBodyFatPercentage] = useState(0);


  useEffect(() => {
    console.log("Errores de validación:", formik.errors);
  }, [formik.errors]);

  useEffect(() => {
    const metricsData = {
      age: formik.values.age,
      gender: formik.values.genre,
      height: formik.values.height,
      neck: recommendation.neck,
      weight: formik.values.weight,
      waist: recommendation.waist,
    };

    const { bmi, bodyFatPercentage } = calculateBodyMetrics(metricsData);

    if (bmi) {
      setImc(bmi);
    }
    if (bodyFatPercentage) {
      setBodyFatPercentage(bodyFatPercentage);
    }
  }, [
    formik.values.weight,
    formik.values.height,
    formik.values.age,
    formik.values.genre,
    recommendation.neck,
    recommendation.waist,
  ]);

  const recommend = () => {
    const exampleData: TrainingRecommendationData = {
      sex: formik.values.genre === "hombre" ? "Hombre" : "Mujer",
      height: formik.values.height,
      heightMetric: "cm",
      weight: formik.values.weight,
      weightMetric: "kg",
      neck: recommendation.neck,
      neckMetric: "cm",
      waist: recommendation.waist,
      waistMetric: "cm",
    };
    if (recommendation.waist !== 0 && recommendation.neck !== 0) {
      const result = getTrainingRecommendation(exampleData);
      console.log(result);
      if (result) {
        formik.setFieldValue("objective", result?.recommendationId);
        setRecommendationVisible(false);
      }
    }

    setRecommendationVisible(false);
  };

  // preload data
  useEffect(() => {
    if (user) {
      setUnidades({
        weight: user_branch?.weight_unit ?? "cm",
        height: user_branch?.measurement_unit ?? "kg",
      });

      // personal information
      formik.setFieldValue("age", user?.data?.age);
      formik.setFieldValue("genre", user?.data?.sex?.toLowerCase());
    }
  }, [user]);

  return (
<Fragment>
      <DialogCreate
        open={visible}
        isLoading={loader}
        OnclickCancel={() => setVisible?.(false)}
        OnclickCreate={formik.handleSubmit}
        setOpen={() => setVisible && setVisible(false)}
        tittleButtonCreate={t("general.create")}
        tittleButtonCancel={t("general.cancel")}
        tittle={t("trainingWizard.createNewTrainingPlan")}
        subTittle={t("trainingWizard.fillFormSubtitle")}
      >
        <form onSubmit={formik.handleSubmit}>
          <div className="mt-6 mx-2 space-y-12">
            {/* Información Personal */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
              <div className="flex items-center justify-center h-full text-center">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("trainingWizard.personalInformation")}
                </h2>
              </div>
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <RadioButtonGroup
                    name="genre"
                    label={t("trainingWizard.selectGender")}
                    options={genreOptions}
                    horizontal
                    onChange={formik.handleChange}
                    value={formik.values.genre}
                    error={formik.errors.genre}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("trainingWizard.age")}
                    type="number"
                    name="age"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.age}
                    error={formik.errors.age}
                    textError={formik.errors.age}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={`${t("trainingWizard.weight")} (${unidades.weight})`}
                    type="number"
                    name="weight"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.weight}
                    error={formik.errors.weight}
                    textError={formik.errors.weight}
                  />
                </div>
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={`${t("trainingWizard.height")} (${unidades.height})`}
                    type="number"
                    name="height"
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.height}
                    error={formik.errors.height}
                    textError={formik.errors.height}
                  />
                </div>
              </div>
            </div>

            {/* Objetivo de Entrenamiento */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3 border-b border-gray-200">
              <div className="flex items-center justify-center h-full text-center">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("trainingWizard.trainingObjective")}
                </h2>
              </div>
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                <div className="col-span-full">
                  <RadioButtonGroup
                    name="objective"
                    label={t("trainingWizard.selectObjective")}
                    options={objectiveOptions}
                    onChange={formik.handleChange}
                    value={formik.values.objective}
                    error={formik.errors.objective}
                  />
                </div>
                <div className="col-span-full">
                  <Button
                    onClick={() => setRecommendationVisible(true)}
                    text={t("trainingWizard.recommend")}
                    iconLeft={faWeightScale}
                    className="w-full justify-center"
                  />
                </div>
              </div>
            </div>

            {/* Experiencia */}
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
              <div className="flex items-center justify-center h-full text-center">
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("trainingWizard.experience")}
                </h2>
              </div>
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                {formik.values.objective === "health" && (
                  <div className="col-span-full">
                    <RadioButtonGroup
                      name="work"
                      label={t("trainingWizard.selectDailyActivity")}
                      options={workOptions}
                      onChange={formik.handleChange}
                      value={formik.values.work}
                      error={formik.errors.work}
                    />
                  </div>
                )}
                {formik.values.objective !== "health" && (
                  <div className="col-span-full">
                    <RadioButtonGroup
                      name="activity"
                      label={t("trainingWizard.selectActivityDescription")}
                      options={activityOptions}
                      onChange={formik.handleChange}
                      value={formik.values.activity}
                      error={formik.errors.activity}
                    />
                  </div>
                )}
                {formik.values.objective === "health" &&
                  (formik.values.work === "sit" ||
                    formik.values.work === "standing") && (
                    <div className="col-span-full">
                      <RadioButtonGroup
                        name="activity"
                        label={t("trainingWizard.selectActivityDescription")}
                        options={activityOptions}
                        onChange={formik.handleChange}
                        value={formik.values.activity}
                        error={formik.errors.activity}
                      />
                    </div>
                  )}
                {trainingPlan && autoActivity === "active" && (
                  <Fragment>
                    <div className="col-span-full">
                      <Slider
                        label={t("trainingWizard.selectExperienceDescription")}
                        min={trainingPlan.min}
                        max={trainingPlan.max}
                        name="experience"
                        onChange={formik.handleChange}
                        value={formik.values.experience || trainingPlan.min}
                        datalist={Object.keys(trainingPlan)
                          .filter((key) => !isNaN(Number(key)))
                          .reduce((acc, key) => {
                            acc[key] = trainingPlan[Number(key)];
                            return acc;
                          }, {} as any)}
                      />
                    </div>
                    {sliderTexts && (
                      <div className="col-span-full">
                        <div className="flex flex-col gap-2 text-center">
                          <p className="text-primary font-bold text-lg">
                            {sliderTexts.label}
                          </p>
                          <p className="text-md text-gray-700">
                            {sliderTexts.description}
                          </p>
                          <p className="text-gray-500 font-light text-sm">
                            {sliderTexts.days}
                          </p>
                          <p className="text-gray-500 font-light text-sm">
                            {sliderTexts.text}
                          </p>
                        </div>
                      </div>
                    )}
                  </Fragment>
                )}
                {trainingPlan &&
                  autoActivity === "sedentary" &&
                  formik.values.objective === "definition" && (
                    <div className="col-span-full">
                      <RadioButtonGroup
                        name="days"
                        label={t("trainingWizard.selectDaysPerWeek")}
                        options={daysOptions}
                        onChange={(e) => {
                          console.log(e.target.value);
                          formik.setFieldValue("days", Number(e.target.value));
                        }}
                        value={formik.values.days}
                        error={formik.errors.days}
                      />
                    </div>
                  )}
                {trainingPlan && formik.values.objective === "health" && (
                  <div className="col-span-full">
                    <RadioButtonGroup
                      name="days"
                      label={t("trainingWizard.selectDaysPerWeek")}
                      options={daysOptions}
                      onChange={(e) =>
                        formik.setFieldValue("days", Number(e.target.value))
                      }
                      value={formik.values.days}
                      error={formik.errors.days}
                    />
                  </div>
                )}

                {formik.values.genre === "mujer" &&
                  autoActivity === "active" &&
                  formik.values.objective === "hypertrophy" &&
                  formik.values.experience && (
                    <div className="col-span-full">
                      <RadioButtonGroup
                        name="cicle"
                        label={t("trainingWizard.menstrualCycleImpact")}
                        options={sicleOption}
                        onChange={formik.handleChange}
                        value={formik.values.cicle}
                        error={formik.errors.cicle}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>
        </form>
      </DialogCreate>

      <Modal
        open={recommendationVisible}
        Close={() => setRecommendationVisible(false)}
        tittleModal={t("trainingWizard.recommend")}
        onClickCreate={() => recommend()}
        textButtonCreate={t("trainingWizard.recommend")}
        textButtonClose={t("general.cancel")}
      >
        <div className="col-span-full mt-4">
          <div className="flex gap-4">
            <InputWithCornerHint
              onChange={(e: any) =>
                setRecommendation({ ...recommendation, waist: e.target.value })
              }
              value={recommendation.waist}
              label={`${t("trainingWizard.waist")} (${unidades.height})`}
              name="waist"
              type="number"
              className="flex-1"
            />
            <InputWithCornerHint
              onChange={(e: any) =>
                setRecommendation({ ...recommendation, neck: e.target.value })
              }
              label={`${t("trainingWizard.neck")} (${unidades.height})`}
              name="neck"
              type="number"
              className="flex-1"
            />
          </div>
        </div>
        <div className="col-span-full mt-4 flex gap-2">
          <div className="p-2 border rounded-md flex-1">
            <InputWithCornerHint
              onChange={formik.handleChange}
              value={formik.values.weight}
              label={`${t("trainingWizard.weight")} (${unidades.weight})`}
              name="weight"
              type="number"
              className="flex-1"
            />
          </div>
          <div className="p-2 border rounded-md flex-1">
            <InputWithCornerHint
              onChange={formik.handleChange}
              value={formik.values.height}
              label={`${t("trainingWizard.height")} (${unidades.height})`}
              name="height"
              type="number"
              className="flex-1"
            />
          </div>
        </div>
        <div className="col-span-full mt-4 flex gap-2">
          <div className="p-2 border rounded-md flex-1">
            <small className="text-gray-500 text-sm">{t("trainingWizard.bmi")}</small>
            <p className="font-bold text-primary text-xl">{imc}</p>
          </div>
          <div className="p-2 border rounded-md flex-1">
            <small className="text-gray-500 text-sm">{t("trainingWizard.bodyFatPercentage")}</small>
            <p className="font-bold text-primary text-xl">
              {bodyFatPercentage}
            </p>
          </div>
        </div>
      </Modal>
    </Fragment>
  );
};
